import { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';

export const useReconcile = (activeProject) => {
  const [accounts, setAccounts] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState('');
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [step, setStep] = useState(0); // Start at dashboard

  // Statement Data
  const [reconcileData, setReconcileData] = useState({
    statementDate: '',
    beginningBalance: 0,
    endingBalance: 0
  });

  // Transactions
  const [dbTransactions, setDbTransactions] = useState([]);
  const [pdfTransactions, setPdfTransactions] = useState([]);
  const [summaryTargets, setSummaryTargets] = useState(null);
  const [showSummaryModal, setShowSummaryModal] = useState(false);
  const [selectedTxIds, setSelectedTxIds] = useState(new Set());
  const [recentDocuments, setRecentDocuments] = useState([]);
  const [savedStatements, setSavedStatements] = useState([]);
  const [monthlyStatus, setMonthlyStatus] = useState(null);

  const fetchAccounts = useCallback(async () => {
    if (!activeProject?._id) return;
    try {
      console.log('Fetching accounts for project:', activeProject._id);
      const res = await axios.get(`/api/projects/${activeProject._id}/accounts`);
      console.log('Raw accounts data:', res.data);
      const bankAccounts = res.data.filter(a => ['Bank', 'Credit Card'].includes(a.type));
      console.log('Filtered bank accounts:', bankAccounts);
      setAccounts(bankAccounts);
      if (bankAccounts.length > 0 && !selectedAccount) {
        setSelectedAccount(bankAccounts[0]._id);
        // Pre-fill beginning balance from account history
        setReconcileData(prev => ({
          ...prev,
          beginningBalance: bankAccounts[0].lastReconciledBalance || 0
        }));
      }
    } catch (err) { 
      console.error('Error fetching accounts:', err); 
    }
  }, [activeProject?._id]); // Removed selectedAccount from dependencies to prevent loops

  const fetchRecentDocuments = useCallback(async () => {
    if (!activeProject?._id || !selectedAccount) return;
    try {
      const res = await axios.get(`/api/projects/${activeProject._id}/documents?accountId=${selectedAccount}&limit=5`);
      setRecentDocuments(res.data);
    } catch (err) { console.error('Error fetching documents:', err); }
  }, [activeProject?._id, selectedAccount]);

  const fetchSavedStatements = useCallback(async () => {
    if (!activeProject?._id || !selectedAccount) return;
    try {
      const res = await axios.get(`/api/projects/${activeProject._id}/reconcile/statements/${selectedAccount}`);
      setSavedStatements(res.data);
    } catch (err) { console.error('Error fetching statements:', err); }
  }, [activeProject?._id, selectedAccount]);

  const fetchMonthlyStatus = useCallback(async () => {
    if (!activeProject?._id || !selectedAccount) return;
    try {
      const res = await axios.get(`/api/projects/${activeProject._id}/reconcile/status/${selectedAccount}?year=${selectedYear}`);
      setMonthlyStatus(res.data);
    } catch (err) { console.error('Error fetching status:', err); }
  }, [activeProject?._id, selectedAccount, selectedYear]);

  useEffect(() => {
    fetchAccounts();
  }, [fetchAccounts]);

  useEffect(() => {
    fetchRecentDocuments();
    fetchSavedStatements();
    fetchMonthlyStatus();
  }, [fetchRecentDocuments, fetchSavedStatements, fetchMonthlyStatus]);

  const startMonthlyReconcile = (monthData) => {
    const { month, year = new Date().getFullYear(), statement } = monthData;
    
    // Default to last day of month if no statement date
    const lastDay = new Date(year, month, 0);
    const defaultDate = (!isNaN(lastDay.getTime())) ? lastDay.toISOString().split('T')[0] : '';

    let statementDate = defaultDate;
    if (statement?.periodEnd) {
      const pDate = new Date(statement.periodEnd);
      if (!isNaN(pDate.getTime())) {
        statementDate = pDate.toISOString().split('T')[0];
      }
    }

    setReconcileData({
      statementDate,
      beginningBalance: monthlyStatus?.lastReconciled?.endingBalance || accounts.find(a => a._id === selectedAccount)?.lastReconciledBalance || 0,
      endingBalance: statement?.endingBalance || 0
    });
    setStep(1); // Go to Setup/Verify step
  };

  // Update beginning balance when account changes
  useEffect(() => {
    const acc = accounts.find(a => a._id === selectedAccount);
    if (acc) {
      setReconcileData(prev => ({
        ...prev,
        beginningBalance: acc.lastReconciledBalance || 0
      }));
    }
  }, [selectedAccount, accounts]);

  const uploadStatement = async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('accountId', selectedAccount);

    setLoading(true);
    setError(null);
    try {
      const res = await axios.post(`/api/projects/${activeProject._id}/reconcile/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      const { extracted, transactions } = res.data;
      setReconcileData(prev => ({
        ...prev,
        statementDate: extracted.statementDate || '',
        beginningBalance: extracted.beginningBalance || prev.beginningBalance,
        endingBalance: extracted.endingBalance || 0
      }));
      setPdfTransactions(transactions);
      setSummaryTargets(extracted.targets || null);
      setShowSummaryModal(true);
      fetchMonthlyStatus(); // Refresh status after upload
      return true;
    } catch (err) {
      setError('Could not parse PDF. Please verify details manually.');
      return false;
    } finally {
      setLoading(false);
    }
  };

  const loadExistingDocument = async (documentId) => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.post(`/api/projects/${activeProject._id}/reconcile/load/${documentId}`);
      const { extracted, transactions } = res.data;
      
      const saved = savedStatements.find(s => s._id === documentId);

      setReconcileData(prev => ({
        ...prev,
        statementDate: extracted.statementDate || (saved?.periodEnd ? new Date(saved.periodEnd).toISOString().split('T')[0] : ''),
        beginningBalance: extracted.beginningBalance || saved?.beginningBalance || prev.beginningBalance,
        endingBalance: extracted.endingBalance || saved?.endingBalance || 0
      }));
      setPdfTransactions(transactions);
      setSummaryTargets(extracted.targets || null);
      setShowSummaryModal(true);
      return true;
    } catch (err) {
      setError('Could not process selected document.');
      return false;
    } finally {
      setLoading(false);
    }
  };

  const fetchDbTransactions = useCallback(async () => {
    setLoading(true);
    try {
      // Fetch un-reconciled transactions for the account
      const res = await axios.get(`/api/projects/${activeProject._id}/transactions?limit=2000&accountId=${selectedAccount}&reconciled=false`);
      const txs = res.data.transactions || (Array.isArray(res.data) ? res.data : []);
      
      // Cutoff logic: QuickBooks style
      // We use the end of the day (23:59:59.999) to ensure transactions on the same day are included.
      const cutoffDate = reconcileData.statementDate 
        ? new Date(reconcileData.statementDate + 'T23:59:59.999Z') 
        : new Date();
      const filteredTxs = txs.filter(t => new Date(t.date) <= cutoffDate);

      setDbTransactions(filteredTxs);
      
      // Smart Multi-Pass Auto-Match Logic
      const autoSelected = new Set();
      const unmatchedPdf = [...pdfTransactions];
      const unmatchedDb = [...filteredTxs];

      const cleanDesc = (d) => (d || '').toLowerCase().replace(/[^a-z0-9]/g, '');

      // Pass 1: High Confidence (Amount + Description + Date Proximity)
      for (let i = unmatchedDb.length - 1; i >= 0; i--) {
        const dbTx = unmatchedDb[i];
        const dbAmt = Math.abs(dbTx.amount).toFixed(2);
        const dbDate = new Date(dbTx.date);
        const dbDesc = cleanDesc(dbTx.description);

        const matchIdx = unmatchedPdf.findIndex(pdfTx => {
            const pAmt = Math.abs(parseFloat(pdfTx.amount || 0)).toFixed(2);
            if (pAmt !== dbAmt) return false;
            
            const pDate = new Date(pdfTx.date);
            const diffDays = Math.abs(pDate - dbDate) / (1000 * 60 * 60 * 24);
            if (diffDays > 4.5) return false;

            return cleanDesc(pdfTx.description).includes(dbDesc) || dbDesc.includes(cleanDesc(pdfTx.description));
        });

        if (matchIdx > -1) {
            autoSelected.add(dbTx._id);
            unmatchedDb.splice(i, 1);
            unmatchedPdf.splice(matchIdx, 1);
        }
      }

      // Pass 2: Description Priority (Amount + Description - ignoring date shift)
      for (let i = unmatchedDb.length - 1; i >= 0; i--) {
        const dbTx = unmatchedDb[i];
        const dbAmt = Math.abs(dbTx.amount).toFixed(2);
        const dbDesc = cleanDesc(dbTx.description);
        if (dbDesc.length < 5) continue; // Skip generic short descriptions

        const matchIdx = unmatchedPdf.findIndex(pdfTx => {
            const pAmt = Math.abs(parseFloat(pdfTx.amount || 0)).toFixed(2);
            if (pAmt !== dbAmt) return false;
            return cleanDesc(pdfTx.description).includes(dbDesc) || dbDesc.includes(cleanDesc(pdfTx.description));
        });

        if (matchIdx > -1) {
            autoSelected.add(dbTx._id);
            unmatchedDb.splice(i, 1);
            unmatchedPdf.splice(matchIdx, 1);
        }
      }

      // Pass 3: Proximity Fallback (Amount + Date Proximity)
      for (let i = unmatchedDb.length - 1; i >= 0; i--) {
        const dbTx = unmatchedDb[i];
        const dbAmt = Math.abs(dbTx.amount).toFixed(2);
        const dbDate = new Date(dbTx.date);

        const matchIdx = unmatchedPdf.findIndex(pdfTx => {
            const pAmt = Math.abs(parseFloat(pdfTx.amount || 0)).toFixed(2);
            if (pAmt !== dbAmt) return false;
            const pDate = new Date(pdfTx.date);
            const diffDays = Math.abs(pDate - dbDate) / (1000 * 60 * 60 * 24);
            return diffDays <= 4.5;
        });

        if (matchIdx > -1) {
            autoSelected.add(dbTx._id);
            unmatchedDb.splice(i, 1);
            unmatchedPdf.splice(matchIdx, 1);
        }
      }

      console.log(`DEBUG: Multi-pass matching complete. Found ${autoSelected.size} matches.`);
      setSelectedTxIds(autoSelected);
      setStep(2);
    } catch (err) {
      setError('Error fetching transactions');
    } finally {
      setLoading(false);
    }
  }, [activeProject?._id, selectedAccount, pdfTransactions, reconcileData.statementDate]);

  const toggleTransaction = (id) => {
    const newSelected = new Set(selectedTxIds);
    if (newSelected.has(id)) newSelected.delete(id);
    else newSelected.add(id);
    setSelectedTxIds(newSelected);
  };

  const finalizeReconcile = async () => {
    setLoading(true);
    try {
      await axios.post(`/api/projects/${activeProject._id}/reconcile/finalize`, {
        accountId: selectedAccount,
        transactionIds: Array.from(selectedTxIds),
        beginningBalance: reconcileData.beginningBalance,
        endingBalance: reconcileData.endingBalance,
        statementDate: reconcileData.statementDate,
        stats
      });
      alert('Reconciliation finalized successfully!');
      setStep(0); // Go back to dashboard
      setSelectedTxIds(new Set());
      fetchAccounts();
      fetchMonthlyStatus();
    } catch (err) {
      alert('Error finalizing reconciliation');
    } finally {
      setLoading(false);
    }
  };

  const stats = useMemo(() => {
    let payments = 0;
    let deposits = 0;
    let hasUncategorized = false;

    dbTransactions.forEach(tx => {
      if (selectedTxIds.has(tx._id)) {
        const isOutflow = tx.fromAccount && (tx.fromAccount._id === selectedAccount || tx.fromAccount === selectedAccount);
        if (isOutflow) payments += Math.abs(tx.amount);
        else deposits += Math.abs(tx.amount);

        if (tx.category === 'Uncategorized') {
          hasUncategorized = true;
        }
      }
    });

    const clearedBalance = reconcileData.beginningBalance + deposits - payments;
    const difference = clearedBalance - reconcileData.endingBalance;

    return { payments, deposits, clearedBalance, difference, hasUncategorized };
  }, [dbTransactions, selectedTxIds, reconcileData, selectedAccount]);

  return {
    accounts, selectedAccount, setSelectedAccount,
    selectedYear, setSelectedYear,
    reconcileData, setReconcileData,
    dbTransactions, selectedTxIds, toggleTransaction,
    loading, error, step, setStep,
    uploadStatement, loadExistingDocument, fetchDbTransactions, finalizeReconcile,
    stats, recentDocuments, savedStatements,
    monthlyStatus, startMonthlyReconcile,
    pdfTransactions,
    summaryTargets, showSummaryModal, setShowSummaryModal
  };
};
